import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ListChecks, Users, FileText, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useTranslation } from 'react-i18next';
import { text } from '@/lib/i18n';

// Declare Paddle types
declare global {
  interface Window {
    Paddle?: {
      Environment: {
        set: (env: 'sandbox' | 'production') => void;
      };
      Initialize: (options: { token: string }) => void;
      Checkout: {
        open: (options: { items: Array<{ priceId: string; quantity?: number }> }) => void;
      };
    };
  }
}

// Helper function to get plan features
function getPlanFeatures(planType: string, plan: any): Array<{ key: string; label: string }> {
  const isFreelancer = planType === 'freelancer';
  const maxTasks = plan.max_tasks || (plan.features?.max_tasks) || (isFreelancer ? 50 : 999999);
  const maxClients = plan.max_clients || (plan.features?.max_clients) || (isFreelancer ? 10 : 999999);
  const maxInvoices = plan.max_invoices || (plan.features?.max_invoices) || (isFreelancer ? 100 : 999999);
  const maxTeamMembers = plan.max_team_members || (plan.features?.max_team_members) || (isFreelancer ? 1 : 10);
  
  const features = [];
  
  if (maxTasks === 999999) {
    features.push({ key: 'tasks', label: 'Neobmedzené úlohy' });
  } else {
    features.push({ key: 'tasks', label: `Maximálne ${maxTasks} úloh` });
  }
  
  if (maxClients === 999999) {
    features.push({ key: 'clients', label: 'Neobmedzení klienti' });
  } else {
    features.push({ key: 'clients', label: `Maximálne ${maxClients} klientov` });
  }
  
  if (maxInvoices === 999999) {
    features.push({ key: 'invoices', label: 'Neobmedzené faktúry' });
  } else {
    features.push({ key: 'invoices', label: `Maximálne ${maxInvoices} faktúr` });
  }
  
  if (maxTeamMembers === 999999) {
    features.push({ key: 'team', label: 'Neobmedzení členovia tímu' });
  } else {
    features.push({ key: 'team', label: `Maximálne ${maxTeamMembers} ${maxTeamMembers === 1 ? 'člen tímu' : 'členov tímu'}` });
  }
  
  return features;
}

export default function Subscribe() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation(['subscribe', 'nav', 'common']);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [paddleLoaded, setPaddleLoaded] = useState(false);

  // Load Paddle.js script
  useEffect(() => {
    if (window.Paddle) {
      setPaddleLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      if (window.Paddle) {
        console.log('✅ Paddle.js loaded successfully');
        setPaddleLoaded(true);
      } else {
        console.error('❌ Paddle.js loaded but window.Paddle is not available');
      }
    };
    script.onerror = () => {
      console.error('❌ Failed to load Paddle.js script');
      toast({
        title: 'Chyba',
        description: 'Nepodarilo sa načítať platobný systém. Obnovte stránku.',
        variant: 'destructive'
      });
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[src="https://cdn.paddle.com/paddle/v2/paddle.js"]');
      if (existingScript) {
        // Don't remove - Paddle.js might be needed elsewhere
      }
    };
  }, [toast]);

  // Use /api/plans endpoint which filters by currency
  const targetCurrency = i18n.language === 'sk' ? 'EUR' : 'USD';
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['/api/plans', i18n.language],
    queryFn: async () => {
      const response = await fetch(`/api/plans?locale=${i18n.language}`);
      if (!response.ok) throw new Error('Failed to fetch plans');
      const data = await response.json();
      console.log('[SUBSCRIBE] Fetched plans:', data);
      return data;
    },
  });

  // Helper function to calculate yearly discount
  const calculateYearlyDiscount = (monthlyPrice: number, yearlyPrice: number) => {
    if (!monthlyPrice || !yearlyPrice) return null;
    const monthlyTotal = monthlyPrice * 12;
    const discount = Math.round((1 - yearlyPrice / monthlyTotal) * 100);
    return discount > 0 ? discount : null;
  };

  // Filter and group plans by type (Freelancer/Business) and billing interval
  const processedPlans = useMemo(() => {
    if (!plansData || !Array.isArray(plansData)) return [];
    
    // Filter plans by currency
    const currencyPlans = plansData.filter((plan: any) => {
      const planCurrency = plan.currency || 'EUR';
      return planCurrency === targetCurrency;
    });
    
    // Group plans by type and billing interval
    const planMap = new Map<string, { monthly?: any; yearly?: any }>();
    
    currencyPlans.forEach((plan: any) => {
      const billingInterval = plan.billing_interval || 'monthly';
      const planName = plan.name.toLowerCase();
      
      // Determine plan type (Freelancer or Business)
      let planType: string;
      if (planName.includes('freelancer')) {
        planType = 'freelancer';
      } else if (planName.includes('business') || planName.includes('bussiness')) {
        planType = 'business';
      } else {
        return; // Skip unknown plan types
      }
      
      if (!planMap.has(planType)) {
        planMap.set(planType, {});
      }
      
      const planGroup = planMap.get(planType)!;
      if (billingInterval === 'monthly' || billingInterval === 'month') {
        planGroup.monthly = plan;
      } else if (billingInterval === 'yearly' || billingInterval === 'year' || billingInterval === 'annually') {
        planGroup.yearly = plan;
      }
    });
    
    // Convert to array format
    const result: any[] = [];
    planMap.forEach((plans, planType) => {
      const selectedPlan = billingInterval === 'monthly' ? plans.monthly : plans.yearly;
      const otherPlan = billingInterval === 'monthly' ? plans.yearly : plans.monthly;
      
      if (selectedPlan) {
        const price = typeof selectedPlan.price === 'string' ? parseFloat(selectedPlan.price) : selectedPlan.price;
        const monthlyPrice = plans.monthly ? (typeof plans.monthly.price === 'string' ? parseFloat(plans.monthly.price) : plans.monthly.price) : 0;
        const yearlyPrice = plans.yearly ? (typeof plans.yearly.price === 'string' ? parseFloat(plans.yearly.price) : plans.yearly.price) : 0;
        
        result.push({
          id: planType,
          name: i18n.language === 'sk' 
            ? (planType === 'freelancer' ? 'Freelancer' : 'Business')
            : (planType === 'freelancer' ? 'Freelancer' : 'Business'),
          price: `${targetCurrency === 'EUR' ? '€' : '$'}${price.toFixed(2)}`,
          period: billingInterval === 'monthly' 
            ? (i18n.language === 'sk' ? 'mesiac' : 'month')
            : (i18n.language === 'sk' ? 'rok' : 'year'),
          paddlePriceId: selectedPlan.paddle_plan_id || '',
          yearlyDiscount: billingInterval === 'yearly' && monthlyPrice && yearlyPrice 
            ? calculateYearlyDiscount(monthlyPrice, yearlyPrice)
            : null,
          features: getPlanFeatures(planType, selectedPlan)
        });
      }
    });
    
    return result;
  }, [plansData, billingInterval, targetCurrency, i18n.language]);
  
  const subscriptionPlans = processedPlans.length > 0 ? processedPlans : [
    // Fallback plans if API fails
    {
      id: 'freelancer',
      name: 'Freelancer',
      price: billingInterval === 'monthly' ? '€9.90' : '€99',
      period: billingInterval === 'monthly' ? t('pricing.billing.monthly') : 'rok',
      paddlePriceId: billingInterval === 'monthly' ? 'pri_01k2qszqf8m6ez5jyz60dzbamd' : 'pri_01k2qt1835hmqjw5cj6p55he9e',
      yearlyDiscount: billingInterval === 'yearly' ? '17%' : null,
      features: [
        'Maximálne 50 klientov',
        'Maximálne 200 úloh', 
        'Maximálne 100 faktúr',
        'Email podpora'
      ]
    },
    {
      id: 'business',
      name: 'Business', 
      price: billingInterval === 'monthly' ? '€12.90' : '€129',
      period: billingInterval === 'monthly' ? t('pricing.billing.monthly') : 'rok',
      paddlePriceId: billingInterval === 'monthly' ? 'pri_01k2qt82ahzchqxjjhk0a0xax3' : 'pri_01k2qt90avm947y935jgbgk9yd',
      yearlyDiscount: billingInterval === 'yearly' ? '17%' : null,
      features: [
        'Maximálne 500 klientov',
        'Maximálne 2000 úloh',
        'Neobmedzené faktúry',
        'Prioritná podpora'
      ]
    }
  ];

  const { data: billingInfo, isLoading: billingLoading, refetch } = useQuery<{
    plan: any;
    payments: any[];
  }>({
    queryKey: ['/api/billing'],
    enabled: !!user,
  });

  const handleSubscribe = async (plan: typeof subscriptionPlans[0]) => {
    console.log('🔥 Paddle Subscribe clicked:', plan.name, plan.paddlePriceId);
    setIsLoading(plan.id);
    
    try {
      const response = await apiRequest('POST', '/api/billing/subscribe', {
        priceId: plan.paddlePriceId,
        planName: plan.name 
      });

      console.log('🔥 Paddle API Response:', response.status, response.statusText);
      
      // Handle non-200 responses first
      if (!response.ok) {
        if (response.status === 401) {
          toast({
            title: 'Nie ste prihlásený',
            description: 'Pre objednávku predplatného sa musíte prihlásiť do aplikácie.',
            variant: 'destructive'
          });
          // Redirect to login after delay
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          return;
        }
        
        // Try to parse error response
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: 'Neznáma chyba' };
        }
        
        toast({
          title: 'Chyba pri vytváraní predplatného',
          description: errorData.error || errorData.message || 'Skúste to prosím znova',
          variant: 'destructive'
        });
        return;
      }
      
      const data = await response.json();
      console.log('🔥 Paddle Response Data:', data);

      if (response.ok && data.checkoutUrl) {
        console.log('✅ Paddle checkout data received:', data.checkoutUrl);
        
        // Check if it's Paddle.js data
        if (data.checkoutUrl.startsWith('paddle-js:')) {
          const checkoutData = JSON.parse(data.checkoutUrl.replace('paddle-js:', ''));
          console.log('🔥 Initializing Paddle.js with data:', checkoutData);
          
          // Initialize Paddle.js Billing v2
          if (typeof window !== 'undefined' && window.Paddle) {
            console.log('🔥 Paddle.js Billing v2 Setup with Client Token:', checkoutData.clientToken ? '***' + checkoutData.clientToken.slice(-4) : 'NOT SET');
            console.log('🔥 Paddle.js loaded:', !!window.Paddle);
            console.log('🔥 paddleLoaded state:', paddleLoaded);
            
            try {
              // Validate price ID before opening checkout
              if (!checkoutData.priceId || !checkoutData.priceId.startsWith('pri_')) {
                throw new Error(`Neplatný price ID: ${checkoutData.priceId}. Price ID musí začínať s "pri_"`);
              }

              if (!checkoutData.clientToken) {
                throw new Error('Client token nie je dostupný. Skontrolujte Paddle konfiguráciu.');
              }

              console.log('🔥 Setting up Paddle.js:', {
                priceId: checkoutData.priceId,
                environment: checkoutData.environment,
                hasClientToken: !!checkoutData.clientToken,
                clientTokenPrefix: checkoutData.clientToken.substring(0, 10) + '...'
              });

              // Paddle.js Billing v2 API - Set environment FIRST (pred Initialize)
              // Dôležité: Environment.set() musí byť pred Initialize()
              const paddleEnv = checkoutData.environment === 'sandbox' ? 'sandbox' : 'production';
              window.Paddle.Environment.set(paddleEnv);
              console.log('✅ Paddle environment set to:', paddleEnv);
              
              // Paddle.js Billing v2 API - Initialize with token
              // Dôležité: NEPRIDÁVAŤ environment do Initialize() - už je nastavené cez Environment.set()
              window.Paddle.Initialize({
                token: checkoutData.clientToken
              });
              console.log('✅ Paddle initialized with client token');

              // Počkáme chvíľu, aby sa Paddle.js úplne inicializoval
              await new Promise(resolve => setTimeout(resolve, 200));

              console.log('🔥 Opening Paddle checkout with:', {
                priceId: checkoutData.priceId,
                environment: checkoutData.environment,
                customerEmail: checkoutData.customerEmail
              });

              // Paddle.js Billing v2 API - Open checkout
              // Podľa dokumentácie: https://developer.paddle.com/paddlejs/methods/paddle-checkout-open
              // Dôležité: successUrl a cancelUrl patria na top level, nie do settings
              const checkoutOptions: any = {
                items: [{ 
                  priceId: checkoutData.priceId, 
                  quantity: 1 
                }],
                // Nastavíme displayMode na overlay (modal)
                settings: {
                  displayMode: 'overlay',
                  // Podľa dokumentácie: len successUrl patrí do settings, cancelUrl nie je podporovaný
                  successUrl: checkoutData.successUrl || `${window.location.origin}/subscribe?success=true`
                }
              };

              // Pridáme customer email ak je dostupný (voliteľné)
              if (checkoutData.customerEmail) {
                checkoutOptions.customer = {
                  email: checkoutData.customerEmail
                };
              }

              // Pridáme custom data ak je dostupné (voliteľné)
              if (checkoutData.customData) {
                checkoutOptions.customData = checkoutData.customData;
              }

              console.log('🔥 Checkout options:', {
                ...checkoutOptions,
                items: checkoutOptions.items,
                hasCustomer: !!checkoutOptions.customer,
                hasCustomData: !!checkoutOptions.customData
              });

              // Paddle.Checkout.open() je synchronná funkcia, ktorá nevracia Promise
              // Používame try-catch namiesto .then()/.catch()
              try {
                window.Paddle.Checkout.open(checkoutOptions);
                console.log('✅ Paddle checkout opened successfully');
              } catch (error: any) {
                console.error('❌ Paddle Checkout.open error:', error);
                console.error('❌ Error details:', {
                  message: error.message,
                  code: error.code,
                  type: error.type,
                  priceId: checkoutData.priceId,
                  environment: checkoutData.environment,
                  fullError: error
                });
                
                // Skúsime získať detailnejšiu chybovú správu
                let errorMessage = error.message || 'Neznáma chyba';
                if (error.detail) {
                  errorMessage += `: ${error.detail}`;
                }
                if (error.errors && Array.isArray(error.errors)) {
                  errorMessage += ` (${error.errors.map((e: any) => e.detail || e.message).join(', ')})`;
                }
                
                toast({
                  title: 'Chyba pri otváraní platobného okna',
                  description: errorMessage || `Price ID "${checkoutData.priceId}" nie je platný. Skontrolujte, či price ID existuje v Paddle ${checkoutData.environment} prostredí.`,
                  variant: 'destructive'
                });
              }
            } catch (error: any) {
              console.error('❌ Error initializing Paddle checkout:', error);
              toast({
                title: 'Chyba',
                description: `Chyba pri otváraní platobného okna: ${error.message || 'Neznáma chyba'}`,
                variant: 'destructive'
              });
            }
          } else {
            console.error('❌ Paddle.js not loaded. paddleLoaded:', paddleLoaded);
            toast({
              title: 'Chyba',
              description: 'Paddle.js nie je načítaný. Počkajte chvíľu a skúste znovu, alebo obnovte stránku.',
              variant: 'destructive'
            });
          }
        } else {
          // Direct redirect to Paddle checkout URL
          window.location.href = data.checkoutUrl;
        }
      }
    } catch (error: any) {
      console.error('🔥 Paddle Subscribe Error:', error);
      toast({
        title: 'Chyba pri vytváraní predplatného', 
        description: error.message || 'Skúste to prosím znova',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(null);
    }
  };

  // Show plans even when not logged in, but redirect to login for subscription
  if (!user) {
    console.log('User not logged in, showing public plans only');
  }

  // Debug log for subscription plans
  console.log('[SUBSCRIBE] Subscription plans:', subscriptionPlans);
  console.log('[SUBSCRIBE] Plans data:', plansData);

  if (billingLoading || plansLoading) {
    return (
      <div className="page-container">
        <div className="section-spacing">
          <div className="flex justify-center items-center h-96">
            <div className="text-center space-y-4">
              <div className="animate-pulse text-lg">Načítavam predplatné...</div>
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (plansLoading) {
    return (
      <div className="page-container">
        <div className="section-spacing text-center">
          <h1 className="page-title text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
            {t('nav:subscribe')}
          </h1>
          <p className="text-muted-foreground text-lg mt-2">Načítavam plány...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (!plansData || (Array.isArray(plansData) && plansData.length === 0)) {
    return (
      <div className="page-container">
        <div className="section-spacing text-center">
          <h1 className="page-title text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
            {t('nav:subscribe')}
          </h1>
          <p className="text-muted-foreground text-lg mt-2">Nepodarilo sa načítať plány predplatného.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="section-spacing text-center">
        <h1 className="page-title text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-primary to-sky-400 bg-clip-text text-transparent">
          {t('nav:subscribe')}
        </h1>
        <p className="text-muted-foreground text-lg mt-2">{t('title')}</p>
      </div>
      
      {/* Billing Toggle - Always visible */}
      <div className="section-spacing">
        <div className="flex justify-center">
          <div className="inline-flex bg-muted rounded-xl p-1 shadow-sm ring-1 ring-border">
            <button
              type="button"
              onClick={() => setBillingInterval('monthly')}
              className={`px-6 sm:px-8 py-2.5 rounded-lg text-base font-semibold transition-all duration-200 ${
                billingInterval === 'monthly'
                  ? 'bg-background text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('monthly')}
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval('yearly')}
              className={`px-6 sm:px-8 py-2.5 rounded-lg text-base font-semibold transition-all duration-200 ${
                billingInterval === 'yearly'
                  ? 'bg-background text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('yearly')}
              <span className="ml-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                {t('discount')}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 w-full">
        {Array.isArray(subscriptionPlans) ? subscriptionPlans.map((plan: any) => (
          <Card key={plan.id} className={`relative border-border/60 shadow-sm ${plan.id==='business' ? 'ring-1 ring-primary/30' : ''}`}>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">{plan.name}</CardTitle>
              <CardDescription>
                <span className="text-4xl font-extrabold text-foreground tracking-tight">{plan.price}</span>
                <span className="text-muted-foreground ml-1">/{plan.period}</span>
                {plan.yearlyDiscount && (
                  <div className="text-sm text-emerald-600 font-medium mt-1">
                    Úspora {plan.yearlyDiscount} oproti mesačnému
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {Array.isArray(plan.features) ? plan.features.map((feature: any, index: number) => {
                  const Icon = feature.key === 'tasks' ? ListChecks
                    : feature.key === 'clients' ? Users
                    : feature.key === 'invoices' ? FileText
                    : feature.key === 'analytics' ? BarChart3
                    : CheckCircle2;
                  return (
                    <li key={index} className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-foreground/80" />
                      <span className="text-sm leading-tight">{feature.label}</span>
                    </li>
                  );
                }) : null}
              </ul>
              
              <Button 
                onClick={() => handleSubscribe(plan)}
                disabled={!!isLoading}
                className="w-full"
                variant={plan.id === 'business' ? 'default' : 'secondary'}
              >
                {isLoading === plan.id ? t('processing') : `${t('choosePlan')} ${plan.name}`}
              </Button>
            </CardContent>
          </Card>
        )) : null}
      </div>
    </div>
  );
}
